import { apiClient } from './api';
import type { 
  Quote, 
  QuoteFilters, 
  QuoteListResponse, 
  QuoteDetailResponse,
  CreateDraftQuoteRequest,
  CreateQuoteRequest,
  UpdateQuoteRequest,
  QuoteDisplayInfo,
  QuotationSummaryResponse,
  LocationValidation
} from '../types/quote';
import type { ComprehensiveQuoteData } from '../types/comprehensiveQuote';

// Re-export types for external use
export type { CreateDraftQuoteRequest, UpdateQuoteRequest } from '../types/quote';

class QuoteService {
  /**
   * Get paginated list of quotes with optional filtering
   */
  async getQuotes(
    page: number = 1, 
    filters: QuoteFilters = {}, 
    limit: number = 25,
    viewAllOrgs: boolean = false
  ): Promise<QuoteListResponse> {
    try {
      const response = await apiClient.getQuotes(page, filters, limit, viewAllOrgs);
      return response;
    } catch (error) {
      console.error('Error fetching quotes:', error);
      throw error;
    }
  }

  /**
   * Get quote statistics by state
   */
  async getQuoteStatistics(viewAllOrgs: boolean = false) {
    try {
      const response = await apiClient.getQuoteStatistics(viewAllOrgs);
      return response;
    } catch (error) {
      console.error('Error fetching quote statistics:', error);
      throw error;
    }
  }

  /**
   * Get a specific quote by ID
   */
  async getQuoteById(id: string): Promise<QuoteDetailResponse> {
    try {
      const response = await apiClient.getQuoteById(id);
      return response;
    } catch (error) {
      console.error('Error fetching quote:', error);
      throw error;
    }
  }

  /**
   * Get comprehensive quote data with all related information
   */
  async getComprehensiveQuoteById(id: string): Promise<ComprehensiveQuoteData> {
    try {
      const response = await apiClient.getComprehensiveQuoteById(id);
      return response;
    } catch (error) {
      console.error('Error fetching comprehensive quote:', error);
      throw error;
    }
  }

  /**
   * Create a draft quote with minimal data (for early wizard creation)
   */
  async createDraftQuote(data: CreateDraftQuoteRequest): Promise<QuoteDetailResponse> {
    try {
      const response = await apiClient.createDraftQuote(data);
      return response;
    } catch (error) {
      console.error('Error creating draft quote:', error);
      throw error;
    }
  }

  /**
   * Create a new quote
   */
  async createQuote(data: CreateQuoteRequest): Promise<QuoteDetailResponse> {
    try {
      const response = await apiClient.createQuote(data);
      return response;
    } catch (error) {
      console.error('Error creating quote:', error);
      throw error;
    }
  }

  /**
   * Update an existing quote
   */
  async updateQuote(id: string, data: UpdateQuoteRequest): Promise<QuoteDetailResponse> {
    try {
      const response = await apiClient.updateQuote(id, data);
      return response;
    } catch (error) {
      console.error('Error updating quote:', error);
      throw error;
    }
  }

  /**
   * Delete a quote (soft delete - changes status)
   */
  async deleteQuote(id: string): Promise<{ message: string }> {
    try {
      const response = await apiClient.deleteQuote(id);
      return response;
    } catch (error) {
      console.error('Error deleting quote:', error);
      throw error;
    }
  }

  /**
   * Delete a draft quote (for abandoned quotes in wizard)
   */
  async deleteDraftQuote(id: string): Promise<{ success: boolean; message: string; data: { deleted: boolean; quoteId: string } }> {
    try {
      const response = await apiClient.deleteDraftQuote(id);
      return response;
    } catch (error) {
      console.error('Error deleting draft quote:', error);
      throw error;
    }
  }

  /**
   * Transform quote data for display with calculated fields
   */
  transformQuoteForDisplay(quote: Quote): QuoteDisplayInfo {
    const now = new Date();
    const coverageStart = new Date(quote.coverageStartDate);
    const coverageEnd = new Date(quote.coverageEndDate);
    const validUntil = new Date(quote.validUntil);

    // Calculate coverage duration in days
    const coverageDuration = Math.ceil(
      (coverageEnd.getTime() - coverageStart.getTime()) / (1000 * 60 * 60 * 24)
    );

    // Calculate days until expiry
    const daysUntilExpiry = Math.ceil(
      (validUntil.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
    );

    // Find primary participant (POLICYHOLDER)
    const primaryParticipant = quote.participants?.find(p => p.role === 'POLICYHOLDER');

    // Determine permissions based on state
    const canEdit = quote.state === 'new';
    const canApprove = quote.state === 'new';
    const canExecute = quote.state === 'approved' || quote.state === 'ready_for_payment_collection';

    return {
      ...quote,
      participantCount: quote.participants?.length || 0,
      primaryParticipant,
      coverageDuration,
      daysUntilExpiry,
      canEdit,
      canApprove,
      canExecute,
    };
  }

  /**
   * Validate quote participant locations against product coverage area
   */
  async validateLocations(quoteId: string): Promise<LocationValidation> {
    try {
      const token = localStorage.getItem('auth-token');
      const response = await fetch(`/api/quotes/${quoteId}/validate-locations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error((errorData as Record<string, string>).message || `Location validation failed (${response.status})`);
      }
      
      const result = await response.json() as {
        data?: { validationResult?: LocationValidation };
      };
      const validation = result.data?.validationResult;
      if (!validation || !Array.isArray(validation.invalid_locations)) {
        throw new Error('Location validation response is missing validationResult');
      }
      return validation;
    } catch (error) {
      console.error('Error validating locations:', error);
      throw error;
    }
  }

  /**
   * Validate farmer geolocations against admin boundaries for a quote
   */
  async validateQuoteGeolocations(quoteId: string): Promise<{
    results: Array<{
      farmerId: string;
      farmerName: string;
      status: string;
      details: {
        validatedAt: string;
        adminUnitUsed: string | null;
        adminLevel: number | null;
        distance: number | null;
        reason: string;
      };
    }>;
    summary: {
      total: number;
      valid: number;
      invalid: number;
      noCoordinates: number;
      noAdminUnit: number;
    };
  }> {
    try {
      const token = localStorage.getItem('auth-token');
      const response = await fetch(`/api/quotes/${quoteId}/validate-geolocations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error((errorData as Record<string, string>).message || `Geolocation validation failed (${response.status})`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error validating quote geolocations:', error);
      throw error;
    }
  }

  /**
   * Get quotation summary with aggregated pricing data
   * Optionally update protection levels
   */
  async getQuotationSummary(
    id: string, 
    protectionLevels?: Record<string, unknown>
  ): Promise<QuotationSummaryResponse> {
    try {
      const response = await apiClient.getQuotationSummary(id, protectionLevels);
      return response;
    } catch (error) {
      console.error('Error getting quotation summary:', error);
      throw error;
    }
  }
}

export const quoteService = new QuoteService();